import { OperationalScale, SocialMediaType } from '@airimpact/types/models/WorkspaceOrganization.model'
import constate from 'constate'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { IUser } from './useUsers'
import { useWorkspaceContext } from './useWorkspace'
import { useZippieIdContext } from './useZippieId'
import apiClient from '../api/apiClient'
import { IFormValues } from '../views/organizations/HandleOrganizationForm'

export enum OrganizationTypes {
  Community = 'Community',
  Organization = 'Organization',
  Governments = 'Governments',
}
export enum CommunitySubTypes {
  'Indigenous Peoples' = 'Indigenous Peoples',
  'Local Communities' = 'Local Communities',
  Smallholders = 'Smallholders',
  'Farmer Groups' = 'Farmer Groups',
}
export enum OrganizationSubTypes {
  Corporations = 'Corporations',
  Businesses = 'Businesses',
  'Private Sector' = 'Private Sector',
  'Non-Profits' = 'Non-Profits',
  'NGOs' = 'NGOs',
  'Academic Institutions' = 'Academic Institutions',
}
export enum GovernmentsSubTypes {
  Local = 'Local',
  Regional = 'Regional',
  National = 'National',
}
export interface IError {
  response: {
    data: {
      error: [
        {
          msg: string
        },
      ]
    }
  }
}
export interface IFile {
  _id: string
  url?: string
  ext: string
}

interface IEmployee {
  _id: string
  _pictureId?: string
  displayName?: string
  phoneNumber?: string
  location?: string
  description?: string
  picture?: IFile
  roles?: string[] | null
}

export type ISocial = {
  type: SocialMediaType
  link: string
}
export interface IOperatingLocations {
  scale: OperationalScale | string
  locations: string[]
}
export interface IContactInformation {
  address: {
    line1: string
    line2?: string
    country: string
  }
  email: string
  phoneNumber: string
  primary: boolean
}
export interface IOrganization {
  _id?: string
  _workspaceId?: number
  _pictureId?: string | null
  _employeeId?: Array<string>
  employees?: IEmployee[]
  workspaceProfile?: boolean
  status?: string // 'Active' | 'Archived'
  name?: string
  description?: string
  picture?: IFile
  website?: string[]
  operatingLocations?: IOperatingLocations
  contactInformation?: IContactInformation[]
  social?: ISocial[]
  deleted?: boolean
  createdAt?: Date
  updatedAt?: Date
  type?: OrganizationTypes | undefined
  subType?: CommunitySubTypes | OrganizationSubTypes | GovernmentsSubTypes | undefined
  locations?: string[]
  projects?: {
    _projectId: string
    _employeeId?: string
    _stakesHolderId?: string
    roles: any
  }[]
}

export const useOrganization = () => {
  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const [organizations, setOrganizations] = useState<IOrganization[]>([])
  const [organization, setOrganization] = useState<IOrganization>({})
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>()
  const [isOrganizationEditMode, setOrganizationIsEditMode] = useState(false)
  const [openAddOrganizationModal, setOpenAddOrganizationModal] = useState(false)
  const [organizationCount, setOrganizationCount] = useState(0)
  const [isVerificationBody, setIsVerificationBody] = useState(false)

  const { uploadFile, selectedNodeId, selectedWorkspaceDetails } = useWorkspaceContext()
  const { isLoggedIn } = useZippieIdContext()
  const [displayedScrolledOrganization, setScrolledDisplayedOrganization] = useState<IOrganization[]>([])

  const [queryOptions, setQueryOption] = useState({
    sortBy: 'createdAt,1',
    skip: 0,
    limit: 6,
    status: ['Active', 'Archived'],
    query: undefined,
  })

  const location = useLocation()

  const handleAddOrganizationModalOpen = () => {
    setOpenAddOrganizationModal(true)
    if (location.pathname.includes('certification/add-certification')) {
      setIsVerificationBody(true)
    } else {
      setIsVerificationBody(false)
    }
  }

  const fetchOrganizations = async () => {
    setIsLoaded(false)
    const { data } = await apiClient.get(`/workspace/${selectedNodeId}/organizations`, {
      params: queryOptions,
    })
    const organizations = data.data.map((node: IOrganization) => ({
      _id: node._id,
      status: node.status,
      name: node.name,
      description: node.description,
      workspaceProfile: node.workspaceProfile,
      _employeeId: node._employeeId,
      employees: node.employees,
      website: node.website,
      operatingLocations: node.operatingLocations,
      contactInformation: node.contactInformation,
      social: node.social,
      _pictureId: node._pictureId,
      picture: node.picture,
    }))
    setOrganizations(organizations)
    setOrganizationCount(data.count)
    setIsLoaded(true)
  }
  const fetchAllOrganizations = async ({ skip, limit }: { skip?: number; limit?: number }) => {
    setIsLoaded(false)
    const { data } = await apiClient.get(`/workspace/${selectedNodeId}/organizations`, {
      params: { ...queryOptions, skip: skip || 0, limit: limit || 6 },
    })

    return data.data
  }

  const getOrganization = async (organizationId: string) => {
    const { data } = await apiClient.get(`/workspace/${selectedNodeId}/organization/${organizationId}`)
    const organizationData = data.data

    const organization = {
      _id: organizationData._id,
      status: organizationData.status,
      name: organizationData.name,
      description: organizationData.description,
      workspaceProfile: organizationData.workspaceProfile,
      _employeeId: organizationData._employeeId,
      employees: organizationData.employees,
      website: organizationData.website,
      operatingLocations: organizationData.operatingLocations,
      contactInformation: organizationData.contactInformation,
      social: organizationData.social,
      _pictureId: organizationData.picture?._id,
      picture: organizationData.picture,
      type: organizationData.type,
      subType: organizationData.subType,
      locations: organizationData.locations,
      projects: organizationData.projects,
    }
    return organization
  }
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const fetchOrganization = async (id?: string) => {
    setIsLoaded(false)

    try {
      const organization = await getOrganization(id || selectedOrganizationId || '')
      setOrganization(organization)
      setIsLoaded(true)
      return organization
    } catch (err) {
      setIsLoaded(false)
      console.error(err)
    }
  }

  const addOrganization = async (organizationDetails: IOrganization) => {
    const response = await apiClient.post(`/workspace/${selectedNodeId}/organization`, organizationDetails)

    await fetchOrganizations()
    return response.data
  }

  const updateOrganization = async (
    organizationDetails: IOrganization,
    organizationId: string | number | undefined,
  ) => {
    const organization = organizations.find((organization) => organization._id === organizationId)
    const newOrganization = {
      ...organization,
      ...organizationDetails,
    }

    delete newOrganization.status
    delete newOrganization._id

    await apiClient.patch(`/workspace/${selectedNodeId}/organization/${organizationId}`, newOrganization)
    await fetchOrganizations()
  }

  const updateContact = async (userDetails: IUser) => {
    await apiClient.patch(`/workspace/${selectedNodeId}/employee/${userDetails.id}`, {
      ...userDetails,
    })
    await fetchOrganizations()
  }

  const deleteOrganization = async (organizationId: string | number) => {
    await apiClient.delete(`/workspace/${selectedNodeId}/organization/${organizationId}`)
    await fetchOrganizations()
  }

  const onQueryChange = (value: string | string[], name: string) => {
    if (name === 'sortBy' || name === 'status') {
      setQueryOption({ ...queryOptions, [name]: value as string[] })
    } else {
      const updatedOptions: any = { ...queryOptions, [name]: value }

      if (name !== 'status') {
        updatedOptions.status = ''
      }

      setQueryOption(updatedOptions)
    }
  }

  const handleAddOrganization = async ({ organizationDetails }: { organizationDetails: IFormValues }) => {
    let details = {}
    if (organizationDetails.img && organizationDetails.img.imageFile) {
      const result = await uploadFile(organizationDetails.img.imageFile, selectedNodeId)
      if (result !== null) {
        const { id } = result
        details = { ...details, _pictureId: id }
      }
    }
    const socials = organizationDetails.social || []
    const response = await addOrganization({
      ...details,
      ...organizationDetails,
      name: organizationDetails.name || '',
      description: organizationDetails.description || '',
      website: organizationDetails.website.filter(Boolean).length > 0 ? organizationDetails.website : [],
      operatingLocations: organizationDetails.operatingLocations,
      contactInformation: organizationDetails.contactInformation || [],
      workspaceProfile: !organizations.length,
      social: socials,
      type: organizationDetails.type,
      subType: organizationDetails.subType,
      locations: [],
    })
    return response
  }

  const handleUpdateOrganization = async ({
    organizationDetails,
    orgId,
  }: {
    organizationDetails: IFormValues
    orgId?: string
  }) => {
    const organization = organizations.find((organization) => organization._id === orgId)

    let _pictureId = null
    if (organizationDetails.img && organizationDetails.img.imageFile) {
      const result = await uploadFile(organizationDetails.img.imageFile, selectedNodeId)
      if (result !== null) {
        const { id } = result
        _pictureId = id
      }
    } else if (organizationDetails.img && organization?.picture?._id) {
      _pictureId = organization?.picture?._id
    }
    const socials = organizationDetails.social || []
    await updateOrganization(
      {
        ...organizationDetails,
        _pictureId,
        name: organizationDetails.name || '',
        description: organizationDetails.description || '',
        website: organizationDetails.website.filter(Boolean).length > 0 ? organizationDetails.website : [],
        operatingLocations: organizationDetails.operatingLocations,
        contactInformation: organizationDetails.contactInformation || [],
        social: socials,
        locations: [],
      },
      orgId || selectedOrganizationId,
    )
  }

  // TODO: remove when Project view is also refactored
  useEffect(() => {
    if (
      selectedNodeId &&
      isLoggedIn &&
      selectedWorkspaceDetails.subscription?.data &&
      location.pathname.includes('project')
    ) {
      fetchOrganizations()
    }
  }, [selectedNodeId, isLoggedIn, queryOptions, selectedWorkspaceDetails, location.pathname])

  useEffect(() => {
    if (selectedOrganizationId) {
      fetchOrganization()
    }
  }, [selectedOrganizationId])

  return {
    openAddOrganizationModal,
    setOpenAddOrganizationModal,
    handleAddOrganizationModalOpen,
    isOrganizationEditMode,
    setOrganizationIsEditMode,
    organizations,
    deleteOrganization,
    selectedOrganizationId,
    setSelectedOrganizationId,
    fetchOrganizations,
    fetchOrganization,
    addOrganization,
    updateOrganization,
    isLoaded,
    organization,
    organizationCount,
    onQueryChange,
    setQueryOption,
    queryOptions,
    setIsVerificationBody,
    isVerificationBody,
    handleAddOrganization,
    handleUpdateOrganization,
    getOrganization,
    updateContact,
    fetchAllOrganizations,
    displayedScrolledOrganization,
    setScrolledDisplayedOrganization,
  }
}

const [OrganizationContainer, useOrganizationContext] = constate(useOrganization)

export { OrganizationContainer, useOrganizationContext }
